import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { railPopoverPosition, TOGGLE_RAIL_ICONS, type ToggleRailItem } from '@mtm/media-studio';
import { ActivatedRoute, Router } from '@angular/router';

import { GetNotificationsOptions, NotificationService } from '../../../shared/services/notification.service';
import { PermissionService } from '../../../shared/services/permissions.service';
import { UnseenNotifierService } from '../../../shared/services/unseen-notifier.service';
import {
  ActiveWorkspaceUpdatedEvent,
  AllNotificationsReadEvent,
  NotificationItem,
  NotificationResult,
  UnseenTotal,
} from '../../../shared/interfaces';
import { CloseAllDropdownsEvent } from '../../../shared/models/events';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { EmitterService } from '../../../shared/services/emitter.service';
import { OverlayService } from '../../../shared/services/overlayService';
import { WorkspaceService } from '../../../shared/services/workspace.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { AuthService } from '../../../shared/services/auth.service';
import { EMPTY_UUID } from '../../../shared/constants';
import { AppStateService } from '../../../shared/services/app-state.service';
import { ClickableNotificationService } from '../../../shared/services/clickable-notification.service';
import { ORG_ELEMENT_TYPES } from '../../../shared/services/notification-helpers/drive-comment.utils';
import { PlatformCommentsChatStateService } from '../../../shared/services/platform-comments-chat-state.service';
import { ProjectSectionService } from '../../../shared/services/project-section.service';
import { ProjectService } from '../../../shared/services/project.service';
import { SubsectionService } from '../../../shared/services/subsection.service';
import { TranslateService } from '../../../shared/services/translate.service';
import { DriveRightRailService } from '../../../shared/drive/drive-right-rail.service';
import { Dialog } from '@angular/cdk/dialog';
import { openContentUnavailableDialog } from '../../../errors/content-unavailable/content-unavailable-dialog.component';

// When true, the unseen counts (`projectId` query) and the list (`projectIds` payload) are scoped to the current project
const shouldIncludeProjectPayload: boolean = false;

@Component({
  selector: 'selector-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.scss',
  standalone: false,
})
export class NotificationDropdown implements AfterViewInit, OnInit, OnDestroy {
  private notifierSub: Subscription = null;
  private projectSelectedSub: Subscription = null;
  private latestNotificationsSub: Subscription = null;
  private readonly statusSignal = signal<string>('');
  private readonly listLoadingSignal = signal<boolean>(false);
  private readonly todaysSignal = signal<any[]>([]);
  private readonly pastSignal = signal<any[]>([]);
  private readonly unseenTotalSignal = signal<number>(0);
  private readonly totalCountSignal = signal<number>(0);
  private readonly mentionCountSignal = signal<number>(0);
  private readonly localProjectIdSignal = signal<string>('');
  private readonly newmessageSignal = signal<boolean>(false);
  private readonly latestMessageSignal = signal<string>('');
  private readonly toastNotificationSignal = signal<any>(null);
  private readonly isButtoneVisibleSignal = signal<boolean>(false);
  private readonly totalNotificationsSignal = signal<number>(0);
  private readonly isDropdownOpenSignal = signal<boolean>(false);
  private readonly activeTabSignal = signal<'all' | 'unread' | 'mentions'>('all');
  private readonly allNotificationsSignal = signal<any[]>([]);

  get activeTab(): 'all' | 'unread' | 'mentions' {
    return this.activeTabSignal();
  }
  set activeTab(value: 'all' | 'unread' | 'mentions') {
    this.activeTabSignal.set(value ?? 'all');
  }

  get allNotifications(): any[] {
    return this.allNotificationsSignal();
  }
  set allNotifications(value: any[]) {
    this.allNotificationsSignal.set(value ?? []);
  }

  get allCount(): number {
    return this.totalCountSignal();
  }

  get unreadCount(): number {
    return this.unseenTotal;
  }

  get mentionsCount(): number {
    return this.mentionCountSignal();
  }

  get status(): string {
    return this.statusSignal();
  }
  set status(value: string) {
    this.statusSignal.set(value ?? '');
  }

  /** List-only loading (tab switch); unlike `status`, it doesn't touch the bell or show the full-screen overlay */
  get listLoading(): boolean {
    return this.listLoadingSignal();
  }

  get todays(): any[] {
    return this.todaysSignal();
  }
  set todays(value: any[]) {
    this.todaysSignal.set(value ?? []);
  }

  get past(): any[] {
    return this.pastSignal();
  }
  set past(value: any[]) {
    this.pastSignal.set(value ?? []);
  }

  get unseenTotal(): number {
    return this.unseenTotalSignal();
  }
  set unseenTotal(value: number) {
    this.unseenTotalSignal.set(value ?? 0);
  }

  get localProjectId(): string {
    return this.localProjectIdSignal();
  }
  set localProjectId(value: string) {
    this.localProjectIdSignal.set(value ?? '');
  }

  get newmessage(): boolean {
    return this.newmessageSignal();
  }
  set newmessage(value: boolean) {
    this.newmessageSignal.set(!!value);
  }

  get latestMessage(): string {
    return this.latestMessageSignal();
  }
  set latestMessage(value: string) {
    this.latestMessageSignal.set(value ?? '');
  }

  get toastNotification(): any {
    return this.toastNotificationSignal();
  }
  set toastNotification(value: any) {
    this.toastNotificationSignal.set(value ?? null);
  }

  get isButtoneVisible(): boolean {
    return this.isButtoneVisibleSignal();
  }
  set isButtoneVisible(value: boolean) {
    this.isButtoneVisibleSignal.set(!!value);
  }

  get totalNotifications(): number {
    return this.totalNotificationsSignal();
  }
  set totalNotifications(value: number) {
    this.totalNotificationsSignal.set(value ?? 0);
  }

  get isDropdownOpen(): boolean {
    return this.isDropdownOpenSignal();
  }
  set isDropdownOpen(value: boolean) {
    this.isDropdownOpenSignal.set(!!value);
  }

  /**
   * Rail placement (drive surfaces): no bell of its own — the bell is a button on the
   * right rail, attached here, and the menu opens beside the rail from a hidden anchor.
   */
  readonly railMode = input(false);
  private readonly dropdown = viewChild(NgbDropdown);
  /** The menu's top-right corner, beside whichever rail (column or panel header) opened it. */
  protected readonly railMenuAt = signal({ top: 0, right: 0 });
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(Dialog);
  private readonly driveRightRail = inject(DriveRightRailService);
  private readonly railItems = computed<readonly ToggleRailItem[]>(() => [
    {
      id: 'notifications',
      icon: TOGGLE_RAIL_ICONS.bell,
      label: this.translateService.data['notifications'] || 'Notifications',
      badge: this.unseenTotalSignal(),
      attention: this.newmessageSignal(),
      active: this.isDropdownOpenSignal(),
    },
  ]);
  private readonly attachToRail = effect((onCleanup) => {
    if (!this.railMode()) {
      return;
    }
    onCleanup(
      this.driveRightRail.attach({
        order: 30,
        items: this.railItems,
        select: (_id, trigger) => {
          if (trigger) {
            this.railMenuAt.set(railPopoverPosition(trigger));
          }
          this.buzzerOff();
          this.dropdown()?.toggle();
        },
      })
    );
  });

  newChatMessage: boolean = false;
  isnotificationServiceMessage: boolean = false;
  ngUnsubscribe = new Subject();
  isHrUser: boolean = false;
  isYslUser: boolean = false;
  userType: string;
  currentUser: any;
  private unseenDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly unseenNotifier: UnseenNotifierService,
    private readonly permissionService: PermissionService,
    private readonly overlayService: OverlayService,
    private readonly workspaceService: WorkspaceService,
    private readonly analyticService: AnalyticsService,
    private readonly authService: AuthService,
    private readonly appState: AppStateService,
    private readonly clickableNotification: ClickableNotificationService,
    private readonly platformChatState: PlatformCommentsChatStateService,
    private readonly projectService: ProjectService,
    private readonly projectSectionService: ProjectSectionService,
    private readonly subsectionService: SubsectionService,
    private readonly elementRef: ElementRef
  ) {
    this.currentUser = this.authService.getAuthUser();
  }

  ngOnInit() {
    //check user type is
    //special case is notifications page
    //here we have to load based on selected project in dropdown
    if (window.location.pathname == '/account/notifications') {
      if ('project' in this.route.snapshot.queryParams) {
        this.localProjectId = this.route.snapshot.queryParams.project;
      }
    }
    this.getUnseenNotificationsCount();

    EmitterService.get(ActiveWorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.getUnseenNotificationsCount();
      });

    EmitterService.get(AllNotificationsReadEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.unseenTotal = 0;
        this.getUnseenNotificationsCount();
      });
  }

  getCurrentProjectId() {
    //edge case for notifications page
    if (window.location.pathname == '/account/notifications') return this.localProjectId;

    let { projectId = '' } = this.route.snapshot.params;
    if (projectId) return projectId;

    //this is used when inside edit project
    ({ projectId = '' } = this.route.firstChild.snapshot.params);
    if (projectId) return projectId;

    return '';
  }

  onDropdownOpen(isOpen: boolean): void {
    this.isDropdownOpen = isOpen;
    if (isOpen) {
      EmitterService.get(CloseAllDropdownsEvent).emit();
      document.querySelectorAll<HTMLElement>('.ql-mention-list-container').forEach((el) => {
        el.style.display = 'none';
      });
    }
  }

  private getUnseenNotificationsCount(callback: () => void | null = null): void {
    if (this.unseenDebounceTimer) {
      clearTimeout(this.unseenDebounceTimer);
    }

    this.status = 'LOADING';

    this.unseenDebounceTimer = setTimeout(() => {
      this.unseenDebounceTimer = null;
      this.fetchUnseenNotificationsCount(callback);
    }, 300);
  }

  private fetchUnseenNotificationsCount(callback: () => void | null = null): void {
    if (!this.elementRef.nativeElement.offsetParent) {
      return;
    }

    const unseenCount$ = this.getUnseenCountObservable();
    if (!unseenCount$) {
      this.status = 'LOADED';
      this.unseenTotal = 0;
      return;
    }

    let receivedUnseenCount: boolean = false;
    unseenCount$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          if (!receivedUnseenCount && this.status === 'LOADING') {
            this.status = 'LOADED';
            this.unseenTotal = 0;
          }
        })
      )
      .subscribe({
        next: (data: UnseenTotal) => {
          receivedUnseenCount = true;
          this.unseenTotal = data.unseenCount ?? 0;
          this.totalCountSignal.set(data.totalCount ?? 0);
          this.mentionCountSignal.set(data.mentionCount ?? 0);
          this.getLatestNotifications();
          if (callback) {
            callback();
          }
        },
      });
  }

  private getUnseenCountObservable(): Observable<UnseenTotal> | null {
    /**
    const projectId = this.getCurrentProjectId();
    const activeWorkspace: any = this.workspaceService.getActiveWorkspace();

    if (projectId?.length) {
      return this.notificationService.getUnseenProjectNotificationsCount(projectId) ?? null;
    }
    if (activeWorkspace?.id) {
      return this.notificationService.getUnseenWorkspaceNotificationsCount(activeWorkspace.id) ?? null;
    }
    */
    // Same project scope as getLatestNotifications(), so the tab badges match the list
    return this.notificationService.getAllUnseenNotificationsFromAPI(this.getScopedProjectId() || undefined) ?? null;
  }

  // The current project when the dropdown is project-scoped, otherwise '' (global)
  private getScopedProjectId(): string {
    return shouldIncludeProjectPayload ? this.getCurrentProjectId() : '';
  }

  getLatestNotifications(showGlobalLoader: boolean = true) {
    const projectId: string = this.getScopedProjectId();
    if (showGlobalLoader) {
      this.status = 'LOADING';
    } else {
      this.listLoadingSignal.set(true);
    }
    const requestProjectId: string | string[] = projectId ? [projectId] : projectId;
    // Drop any in-flight request so a slow response for a previous tab can't overwrite this one
    this.latestNotificationsSub?.unsubscribe();
    this.latestNotificationsSub = this.notificationService
      .getNotifications({ projectId: requestProjectId, ...this.getTabFilter() })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: NotificationResult) => {
          const items: NotificationItem[] = result.data ?? [];
          // Keep no-project items (the BE counts them in this project's scope); only hide the ones
          // prepareNotification backfilled to a different project (STUDIO-1177)
          const inScope: NotificationItem[] = projectId
            ? items.filter(
                (n: NotificationItem) => !n.projectId || n.projectId === EMPTY_UUID || n.projectId === projectId
              )
            : items;
          // Always apply, even when empty: an empty tab must not keep showing the previous tab's items
          this.handleNotificationsUpdate(inScope);
          this.setListLoaded();
        },
        error: () => {
          this.setListLoaded();
        },
      });
  }

  // Clears both loaders: this request may have cancelled one started with the other loader
  private setListLoaded(): void {
    this.status = 'LOADED';
    this.listLoadingSignal.set(false);
  }

  private getTabFilter(): Pick<GetNotificationsOptions, 'seen' | 'mentions'> {
    if (this.activeTab === 'unread') return { seen: false };
    if (this.activeTab === 'mentions') return { mentions: true };
    return {};
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.status === 'LOADING' && this.elementRef.nativeElement.offsetParent) {
      this.getUnseenNotificationsCount();
    }
  }

  ngAfterViewInit() {
    this.notifierSub = this.unseenNotifier.subscribe((data) => {
      const oldTotal = this.unseenTotal;
      this.getUnseenNotificationsCount(() => {
        if (this.unseenTotal != oldTotal) {
          this.newmessage = true;
        } else {
          this.newmessage = false;
        }
      });
    });

    this.projectSelectedSub = EmitterService.get('localProject.selected').subscribe((projectId) => {
      this.localProjectId = projectId;
      this.getUnseenNotificationsCount();
    });
  }

  ngOnDestroy() {
    if (this.unseenDebounceTimer) {
      clearTimeout(this.unseenDebounceTimer);
    }

    if (this.notifierSub) this.notifierSub.unsubscribe();

    if (this.projectSelectedSub) this.projectSelectedSub.unsubscribe();

    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  isVisited() {
    if (this.router.url == `/account/notifications`) {
      return 'notification-dropdown-visited';
    }
    return '';
  }

  goToNotification() {
    sessionStorage.setItem('last_visited_url', this.router.url);
    // const projectId = this.getCurrentProjectId();
    const params = {};
    // if (projectId) {
    //   params['project'] = projectId;
    // }
    this.router.navigate(['account', 'notifications'], { queryParams: params });
  }

  /*
		private handleUnseenUpdate(data: any) {
			if (!data)
				return;

			const projectId = this.getCurrentProjectId();
			let total = 0;
			for (let key in data) {
				if (projectId && key != projectId)
					continue;
				const { unseenCount = 0 } = data[key];
				total += unseenCount;
			}
			this.unseenTotal = total;
		}

	 */

  setActiveTab(tab: 'all' | 'unread' | 'mentions') {
    this.activeTab = tab;
    this.getLatestNotifications(false);
  }

  getItemContextTitle(n: any): string {
    const round = n?.object?.roundName || n?.object?.stageName || (n?.object?.sectionName ? n.object.sectionName.toUpperCase() : '');
    const project = n?.object?.projectName ? n.object.projectName.toUpperCase() : (n?.object?.elementName ? n.object.elementName.toUpperCase() : '');
    if (round && project) return `${round} · ${project}`;
    if (round) return round;
    if (project) return project;
    return '';
  }

  getChannelName(n: any): string {
    return n?.object?.channelName || n?.object?.sectionName || 'General';
  }

  getRelativeTime(dateStr: string): string {
    if (!dateStr) return '18 min';
    const now = new Date().getTime();
    const date = new Date(dateStr).getTime();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (isNaN(diffInSeconds) || diffInSeconds <= 0) return 'just now';
    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    const minutes = Math.floor(diffInSeconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h`;
    const days = Math.floor(hours / 24);
    return `${days} d`;
  }

  goToSettings() {
    this.router.navigate(['account', 'notification-settings']);
  }

  markSingleAsRead(event: Event, item: any) {
    event.stopPropagation();
    // Counts come from the BE now; keep the Unread badge in step until the next refresh
    if (!item.seen) {
      this.unseenTotal = Math.max(0, this.unseenTotal - 1);
    }
    item.seen = true;
    this.notificationService.markNotificationsAsSeen(item.projectId).subscribe();
  }

  deleteNotificationItem(event: Event, item: any) {
    event.stopPropagation();
    this.allNotifications = this.allNotifications.filter((n) => n !== item);
    this.filterNotifications();
  }

  muteNotification(event: Event, item: any) {
    event.stopPropagation();
    if (this.overlayService) {
      this.overlayService.showSuccessWithTimeout('Notification muted');
    }
  }

  replyNotification(event: Event, item: any) {
    event.stopPropagation();
    this.goToNotification();
  }

  onNotificationItemClick(event: Event, item: any) {
    this.markSingleAsRead(event, item);
    // The target may be the current page (org feed), where no navigation closes the menu
    this.dropdown()?.close();
    this.goToNotificationSource(item);
  }

  /**
   * Routes to what the notification is about instead of the notifications page.
   * Mirrors `NotificationViewComponent.onNotificationClick`; keep the two in
   * sync until they share one implementation.
   */
  private goToNotificationSource(item: any): void {
    const obj: any = item?.object || {};

    if (obj.channelUuid && obj.messageUuid) {
      this.openCommentsPanel();
      this.platformChatState.requestNavigation({
        channelId: obj.channelUuid,
        messageId: obj.messageUuid,
        parentMessageId: obj.parentMessageUuid,
        targetId: this.clickableNotification.getNotificationTargetId(obj),
      });
    }

    if (typeof item?.code === 'string' && item.code.startsWith('EXT_TASK_')) {
      void this.clickableNotification.goToTaskPage(item);
      return;
    }

    const projectId: string =
      item?.projectId && item.projectId !== EMPTY_UUID ? item.projectId : obj.projectId || '';
    if (!projectId) {
      // No-project notifications (e.g. SHARE_INVITE_SENT) may carry a BE-provided route
      if (typeof obj.path === 'string' && obj.path) {
        void this.router.navigateByUrl(obj.path);
        return;
      }
      if (ORG_ELEMENT_TYPES.has(obj.elementType)) {
        this.clickableNotification.navigateToOrgEntity(obj);
        return;
      }
      this.goToNotification();
      return;
    }

    if (this.projectService.project?.id === projectId) {
      this.clickableNotification.gotoNotificationSource(this.projectService.sections, item);
      return;
    }
    // The project fetch only confirms it still exists / is accessible before leaving the current screen.
    forkJoin([
      this.projectSectionService.getSectionsAndSubsections(projectId),
      this.projectService.getSingleProject(projectId),
    ]).subscribe({
      next: ([sections]: [any, unknown]) => {
        this.projectService.leaveFromProject();
        this.clickableNotification.gotoNotificationSource(sections, item);
      },
      error: () => openContentUnavailableDialog(this.dialog),
    });
  }

  private openCommentsPanel(): void {
    const activePage: string = this.appState.getActivePage();
    if (activePage) {
      this.appState.setCommentPageCommentExpanded(activePage, true);
    }
    this.subsectionService.updateUIState({ commentsCollapsed: false, rightPanelView: 'comment' });
  }

  private handleNotificationsUpdate(items: any[]) {
    if (this.isHrUser || this.isYslUser) {
      this.totalNotifications = items ? items.length : 0;
    }

    if (!items) {
      this.allNotifications = [];
      this.todays = [];
      this.past = [];
      return;
    }

    this.allNotifications = items;
    this.filterNotifications();
  }

  filterNotifications() {
    // Tab filtering (unread / mentions) is done by the BE in getLatestNotifications()
    const filtered = this.allNotifications;
    const todayItems = filtered.filter((i) => i.isToday && i.notificationDisplay?.label);
    const pastItems = filtered.filter((i) => !i.isToday && i.notificationDisplay?.label);
    this.todays = todayItems.slice(0, 4);
    this.past = pastItems.slice(0, 4 - this.todays.length);

    this.notificationService.changeMessage(
      this.todays.length > 0
        ? this.todays[0].notificationDisplay?.label
        : this.past.length
          ? this.past[0].notificationDisplay?.label
          : ''
    );
  }

  buzzerOff() {
    this.workspaceService.currentStatus.subscribe((val) => (this.isButtoneVisible = val));
    this.newmessage = false;
  }

  markNotificationsAsRead() {
    // Same scope as the list and the badge: global unless the dropdown is project-scoped
    const projectId = this.getScopedProjectId();
    this.notificationService.markNotificationsAsSeen(projectId).subscribe(() => {
      EmitterService.get(AllNotificationsReadEvent).emit({ projectId });
    });
  }
}
